import {memo, useCallback, useEffect, useMemo, useState} from "react";
import {MapContainer, Marker, ScaleControl, Tooltip, useMap, ZoomControl} from "react-leaflet";
import ReactLeafletGoogleLayer from 'react-leaflet-google-layer';
import {Icon, LatLng} from "leaflet";
import FloorControl from "./controls/FloorControl";
import ZoomListener from "./controls/ZoomListener";

const GoogleMapLoader = ({
                             isEliOfficeLocation,
                             isTestLocation,
                             isMYLocation,
                             allLocationMarkers = [],
                             updateLocations = true,
                             showAllLocations = false,
                             overview = false, onSelect, selectedCall,
                             locationMarker = {}
                         }) => {
    const [mapLoading, setMapLoading] = useState(true);
    const [selectedFloor, setSelectedFloor] = useState(() => locationMarker?.floor ?? null);

    const EmlMarkerIcon = (isLast) => new Icon({
        iconUrl: '/static/img/eml-marker.png',
        iconSize: [54, 54],
        className: isLast ? "" : "grayscale-80"
    })

    const floors = useMemo(() => isEliOfficeLocation ? [
        {name: "F1", url: "/static/img/1.pmtiles", value: "1"},
        {name: "F2", url: "/static/img/2.pmtiles", value: "2"},
        {name: "F3", url: "/static/img/3.pmtiles", value: "3"}
    ] : isTestLocation ? [
        {name: "B1", url: "/static/img/MB_B1.tif", value: "-1"},
        {name: "F1", url: "/static/img/MB_F1.tif", value: "1"},
        {name: "F2", url: "/static/img/MB_F2.tif", value: "2"},
        {name: "F3", url: "/static/img/MB_F3.tif", value: "3"},
        {name: "F4", url: "/static/img/MB_F4.tif", value: "4"}
    ] : isMYLocation ? [
        {name: "F2", url: "/static/img/MY-F2.tif", value: "1"}
    ] : [], [isEliOfficeLocation, isTestLocation, isMYLocation]);

    useEffect(() => {
        if (isEliOfficeLocation) {
            setMapLoading(true);
            return;
        }
        setMapLoading(false);
    }, [isEliOfficeLocation, setMapLoading]);

    useEffect(() => {
        setSelectedFloor(locationMarker?.floor ?? null);
    }, [locationMarker]);

    const setLoaded = useCallback((loaded) => setMapLoading(!loaded), [setMapLoading]);

    const currentFloor = useMemo(() => {
        if (!floors.length) {
            return null;
        }

        const floorValue = updateLocations ? locationMarker?.floor : selectedFloor;
        return floors.find((floor) => floor.value === floorValue) ?? floors[0];
    }, [floors, locationMarker?.floor, selectedFloor, updateLocations]);

    const displayMarker = locationMarker?.id && (
        !floors.length || locationMarker?.type === "gps" ||
        locationMarker?.floor === currentFloor?.value
    );
    const mapLoaded = !mapLoading;
    return <>
        <div className="relative flex-1">
            {mapLoading && <div className="absolute top-0 left-0 z-[1000] h-1.5 w-full bg-blue-100 overflow-hidden">
                <div className="animate-progress w-full h-full bg-blue-300 origin-left-right"></div>
            </div>}
            <MapContainer attributionControl={false}
                          center={EARTH_CENTER_LAT_LNG}
                          zoom={3}
                          scrollWheelZoom={true}
                          zoomControl={false}
            >
                <ReactLeafletGoogleLayer apiKey='AIzaSyADlCDm6O5btPvU5PzWPbBBJfsSataS6ds' type={'satellite'}/>
                <MapSizeObserver/>
                <ScaleControl position="bottomright"/>
                <ZoomControl position="topright"/>
                {(!overview || floors.length > 0) && <ZoomListener mapLoaded={mapLoaded}
                    focusMarker={overview ? allLocationMarkers[0] : undefined}/>}
                {overview && !floors.length && <OverviewBounds markers={allLocationMarkers}/>}
                <MarkerFlyToListener mapLoaded={mapLoaded}
                                     displayMarker={!overview && !showAllLocations && displayMarker}
                                     locationMarker={locationMarker}/>
                <FloorControl floors={floors}
                              hideSelector={overview}
                              setLoaded={setLoaded}
                              handleSelectFloor={setSelectedFloor}
                              disableSelection={overview || updateLocations}
                              currentFloor={currentFloor}/>
                {showAllLocations && allLocationMarkers.map((marker) => (
                    <Marker key={`e-${marker.id}`}
                            position={[marker.lat, marker.lng]}
                            opacity={selectedCall && marker.callUuid !== selectedCall ? 0.55 : 1}
                            icon={EmlMarkerIcon(marker.isLatest)}
                            eventHandlers={{
                                click: (event) => {
                                    event.target.openTooltip();
                                    onSelect?.(marker.callUuid);
                                }
                            }}>
                        <Tooltip direction="top" offset={[0, -20]} className="show-all-marker-label">
                            {marker.timestampLabel}
                        </Tooltip>
                    </Marker>
                ))}
                {!showAllLocations && displayMarker && (
                    <Marker key={`e-${locationMarker.id}`}
                            position={[locationMarker.lat, locationMarker.lng]}
                            icon={EmlMarkerIcon(true)}>
                        <Tooltip>{locationMarker.id}</Tooltip>
                    </Marker>
                )}
            </MapContainer>
        </div>
    </>
};

export default memo(GoogleMapLoader);
const EARTH_CENTER_LAT_LNG = {lat: 54.331444157894936, lng: -108.15223571159713};

const MarkerFlyToListener = memo(({mapLoaded, displayMarker, locationMarker}) => {
    const map = useMap();

    useEffect(() => {
        if (!mapLoaded || !displayMarker || locationMarker?.lat == null || locationMarker?.lng == null) {
            return;
        }

        map.flyTo(new LatLng(locationMarker.lat, locationMarker.lng), map.getZoom(), {animate: false});
    }, [displayMarker, locationMarker?.lat, locationMarker?.lng, map, mapLoaded]);

    return null;
});

// Fit geographic group markers together instead of flying to one selected call.
// A coordinate-only dependency avoids resetting the camera for label/selection changes.
function OverviewBounds({markers}) {
    const map = useMap();
    const boundsKey = JSON.stringify(markers.map(m => [m.lat, m.lng]));
    useEffect(() => {
        const points = JSON.parse(boundsKey).filter(p => p.every(v => v != null && Number.isFinite(Number(v))));
        if (points.length) map.fitBounds(points, {padding: [40, 40], maxZoom: 19});
    }, [map, boundsKey]);
    return null;
}

// Leaflet needs notification when panel toggles change its container dimensions.
function MapSizeObserver() {
    const map = useMap();
    useEffect(() => {
        const observer = new ResizeObserver(() => map.invalidateSize({pan: false}));
        observer.observe(map.getContainer());
        return () => observer.disconnect();
    }, [map]);
    return null;
}
